/**
 * Navigation utilities for test modules
 * Provides consistent routing across mock tests, mini mocks, and class tests
 */

import { useLocation } from "react-router-dom";
import { TEST_TYPES, TEST_ROUTES, TEST_BACK_ROUTES } from "../mockTestSlice";

/**
 * Hook to get navigation paths based on current test type
 * Reads from location state or derives from URL path
 */
export function useTestNavigation() {
  const location = useLocation();
  const state = location.state || {};
  
  // Try to get from state first, then derive from URL
  const testType = state.testType || getTestTypeFromPath(location.pathname);
  const baseRoute = state.baseRoute || TEST_ROUTES[testType] || "/mock-test";
  const backRoute = state.backRoute || TEST_BACK_ROUTES[testType] || "/app/mocktests";

  return {
    testType,
    baseRoute,
    backRoute,
    // Helper to build test navigation URL
    getTestUrl: (testId, section = "") => {
      if (section) {
        return `${baseRoute}/${testId}/${section}`;
      }
      return `${baseRoute}/${testId}`;
    },
    // Helper to build results URL
    getResultsUrl: (testId) => `${baseRoute}/${testId}/results`,
  };
}

/**
 * Derive test type from URL path
 */
export function getTestTypeFromPath(pathname) {
  if (pathname.includes("/bundle-test/") || pathname.includes("/bundle-test")) {
    return TEST_TYPES.BUNDLE_TEST;
  }
  if (pathname.includes("/class-test/") || pathname.includes("/classtests")) {
    return TEST_TYPES.CLASS_TEST;
  }
  if (pathname.includes("/mini-mock-test/") || pathname.includes("/minimocktests")) {
    return TEST_TYPES.MINI_MOCK;
  }
  return TEST_TYPES.MOCK_TEST;
}

/**
 * Get navigation state with test type info
 * Use this when navigating to preserve test type context
 */
export function getNavigationState(currentState, testType, baseRoute, backRoute) {
  const resolvedTestType = testType || currentState?.testType || TEST_TYPES.MOCK_TEST;
  return {
    ...currentState,
    testType: resolvedTestType,
    baseRoute: baseRoute || currentState?.baseRoute || TEST_ROUTES[resolvedTestType] || "/mock-test",
    backRoute: backRoute || currentState?.backRoute || TEST_BACK_ROUTES[resolvedTestType] || "/app/mocktests",
  };
}
